import {
  ApiError,
  type Attachment,
  INLINE_IMAGE_CONTENT_TYPES,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_ITEM,
} from '@mevaser/shared'
import { Button, useToast } from '@mevaser/ui'
import { File as FileIcon, Paperclip, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { api } from '../lib/api.js'

/**
 * Pasting a picture and attaching files on tasks, wiki pages, workspace posts
 * and ticket notes (t1249). A file is uploaded the moment it is chosen and
 * only *named* when the item is saved (`attachmentIds`), the same two-step the
 * ticket text composer uses for pictures.
 */

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const isPicture = (contentType: string) =>
  (INLINE_IMAGE_CONTENT_TYPES as readonly string[]).includes(contentType)

/** Which kind of item a file is staged for — decides who may upload it. */
export type AttachmentKind = 'task' | 'wiki' | 'workspace'

/** Uploads one picture for pasting into rich text and returns the URL to embed. */
export function inlineImageUploader(kind: AttachmentKind) {
  return async (file: File): Promise<string> => {
    const form = new FormData()
    form.append('file', file)
    const uploaded = await api.postFile<Attachment>(`/attachments?for=${kind}&inline=1`, form)
    return uploaded.url
  }
}

interface UploadsOptions {
  /** Where a chosen file is posted. */
  endpoint: string
  /** The path that discards a staged file; defaults to the generic one. */
  deletePath?: (id: string) => string
  /** Turns the upload response into what the chips show. */
  normalize?: (raw: Attachment) => Attachment
  maxBytes?: number
  maxCount?: number
}

export interface FileUploads {
  files: Attachment[]
  ids: string[]
  uploading: boolean
  upload: (picked: File[]) => Promise<void>
  remove: (id: string) => Promise<void>
  /** Replaces the list, e.g. with an item's saved files when its editor opens. */
  reset: (next?: Attachment[]) => void
  /** Ctrl/Cmd+V with a picture on the clipboard attaches it; pasting text is untouched. */
  handlePaste: (e: React.ClipboardEvent) => void
}

export function useFileUploads({
  endpoint,
  deletePath = (id) => `/attachments/${id}`,
  normalize = (raw) => raw,
  maxBytes = MAX_ATTACHMENT_BYTES,
  maxCount = MAX_ATTACHMENTS_PER_ITEM,
}: UploadsOptions): FileUploads {
  const toast = useToast()
  const [files, setFiles] = useState<Attachment[]>([])
  const [uploading, setUploading] = useState(false)
  // Files that already belong to the item are only dropped from the list; the
  // save removes them. Only files staged in this session are discarded now.
  const saved = useRef(new Set<string>())

  async function upload(picked: File[]) {
    if (picked.length === 0) return
    setUploading(true)
    let count = files.length
    try {
      for (const file of picked) {
        if (count >= maxCount) {
          toast.error('Could not attach', `At most ${maxCount} files can be attached.`)
          break
        }
        if (file.size === 0) {
          toast.error('Could not attach', `${file.name || 'That file'} is empty.`)
          continue
        }
        if (file.size > maxBytes) {
          toast.error(
            'Could not attach',
            `${file.name || 'That file'} is larger than ${Math.round(maxBytes / 1024 / 1024)} MB.`,
          )
          continue
        }
        const form = new FormData()
        form.append('file', file)
        const uploaded = await api.postFile<Attachment>(endpoint, form)
        setFiles((prev) => [...prev, normalize(uploaded)])
        count += 1
      }
    } catch (error) {
      toast.error(
        'Could not attach the file',
        error instanceof ApiError ? error.message : undefined,
      )
    } finally {
      setUploading(false)
    }
  }

  async function remove(id: string) {
    if (saved.current.has(id)) {
      setFiles((prev) => prev.filter((f) => f.id !== id))
      return
    }
    try {
      await api.delete(deletePath(id))
      setFiles((prev) => prev.filter((f) => f.id !== id))
    } catch (error) {
      toast.error(
        'Could not remove the file',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  }

  function reset(next: Attachment[] = []) {
    saved.current = new Set(next.map((f) => f.id))
    setFiles(next)
  }

  function handlePaste(e: React.ClipboardEvent) {
    const pictures = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'))
    if (pictures.length === 0) return
    e.preventDefault()
    void upload(namePasted(pictures))
  }

  return { files, ids: files.map((f) => f.id), uploading, upload, remove, reset, handlePaste }
}

/** The clipboard names every screenshot `image.png`; give each its own name. */
export function namePasted(pictures: File[]): File[] {
  return pictures.map((f, i) =>
    f.name && f.name !== 'image.png'
      ? f
      : new File([f], `pasted-image-${Date.now()}-${i + 1}.${f.type.split('/')[1] ?? 'png'}`, {
          type: f.type,
        }),
  )
}

/** "Attach file" button with its hidden picker. */
export function AttachButton({
  uploads,
  disabled,
  label = 'Attach file',
}: {
  uploads: FileUploads
  disabled?: boolean
  label?: string
}) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          void uploads.upload(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled || uploads.uploading}
        onClick={() => input.current?.click()}
      >
        <Paperclip className="size-4" aria-hidden="true" />
        {uploads.uploading ? 'Attaching…' : label}
      </Button>
    </>
  )
}

/** Files staged or saved on an item that is being edited, each removable. */
export function AttachmentChips({ uploads }: { uploads: FileUploads }) {
  if (uploads.files.length === 0) return null
  return (
    <ul className="flex flex-wrap gap-2">
      {uploads.files.map((f) => (
        <li
          key={f.id}
          className="flex max-w-full items-center gap-2 rounded-md border border-border bg-surface px-2 py-1 text-xs"
        >
          {isPicture(f.contentType) ? (
            <img src={f.url} alt="" className="size-8 rounded object-cover" />
          ) : (
            <FileIcon className="size-4 shrink-0 text-foreground-muted" aria-hidden="true" />
          )}
          <span className="truncate">{f.filename}</span>
          <span className="shrink-0 text-foreground-muted">{formatFileSize(f.sizeBytes)}</span>
          <button
            type="button"
            aria-label={`Remove ${f.filename}`}
            className="shrink-0 rounded-full p-0.5 text-foreground-muted hover:text-foreground"
            onClick={() => void uploads.remove(f.id)}
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  )
}

/** Read-only view of an item's files: pictures as thumbnails, the rest as downloads. */
export function AttachmentList({
  files,
  compact = false,
}: {
  files: Attachment[] | undefined
  compact?: boolean
}) {
  if (!files || files.length === 0) return null
  const pictures = files.filter((f) => isPicture(f.contentType))
  const others = files.filter((f) => !isPicture(f.contentType))
  return (
    <div className="mt-2 space-y-2">
      {pictures.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {pictures.map((f) => (
            <a key={f.id} href={f.url} target="_blank" rel="noreferrer" title={f.filename}>
              <img
                src={f.url}
                alt={f.filename}
                className={
                  compact
                    ? 'max-h-40 max-w-full rounded-md border border-border'
                    : 'max-h-64 max-w-full rounded-md border border-border'
                }
              />
            </a>
          ))}
        </div>
      ) : null}
      {others.length > 0 ? (
        <ul className="space-y-1">
          {others.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-sm">
              <FileIcon className="size-4 shrink-0 text-foreground-muted" aria-hidden="true" />
              <a
                href={f.url}
                download={f.filename}
                className="truncate text-primary hover:underline"
              >
                {f.filename}
              </a>
              <span className="shrink-0 text-xs text-foreground-muted">
                {formatFileSize(f.sizeBytes)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
